import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import {
  HeaderRoot,
  HeaderButton,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { ensureQueryData } from "@/hooks/use-graphql";
import { AppointmentPatientSwapForm } from "@/components/appointment/patient/appointment-patient-swap-form";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";

import type { JSX } from "react";

const PatientSwapPageQuery = graphql(`
  query PatientSwapPageQuery($codAgendamento: Int!) {
    agendamento: agendamentos_by_pk(codAgendamento: $codAgendamento) {
      codAgendamento
      codAgendamentoRecorrente
      data
      horaInicio
      codFinalidadeAgendamento
      horaFim
      ...AppointmentPatientSwapFormFragment
    }
  }
`);

const patientSwapPageSearchSchema = z.object({
  action: z.string().optional().catch(""),
  scope: z.enum(["single", "all"]).catch("single"),
});

const PatientSwapPage = (): JSX.Element => {
  const { appointment, codAgendamentoRecorrente } = Route.useLoaderData();

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Trocar paciente" align="center" />
      </HeaderRoot>

      <Page>
        {appointment ? (
          <AppointmentPatientSwapForm
            queryData={appointment}
            codAgendamentoRecorrente={codAgendamentoRecorrente ?? null}
          />
        ) : (
          <FullPageSpinner />
        )}
      </Page>
    </>
  );
};

export const Route = createFileRoute("/appointment/$appointmentId/patient-swap")({
  component: PatientSwapPage,
  validateSearch: patientSwapPageSearchSchema,
  parseParams: (params) => ({
    appointmentId: z.int().parse(Number(params.appointmentId)),
  }),
  loader: async (opts) => {
    try {
      const variables = {
        codAgendamento: opts.params.appointmentId,
      };

      const data = await ensureQueryData(opts.context, PatientSwapPageQuery, variables);

      const appointment = data.agendamento;

      const appointmentNotFound = !appointment;

      if (appointmentNotFound) throw new Error("Appointment not found!");

      return {
        appointment,
        codAgendamentoRecorrente: appointment.codAgendamentoRecorrente,
      };
    } catch (_e) {
      redirect({
        to: "/calendar",
        throw: true,
      });
    }

    return {};
  },
});
